import { useState, useEffect, useCallback } from 'react';
import { Award, Plus, Trash2, X, Check, ShieldCheck, Server, Loader2, Home, MessageSquare, Copy, Store, Sparkles } from 'lucide-react';
import { apiService, LuckPermsGroupDTO, GroupNodeDTO, PackContainer, EssentialsData } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import { useSnapshotDraft, getDraftPayload, draftKey } from '@/stores/unsavedDrafts.store';
import { MinecraftText } from '@/components/config/MinecraftText';
import { GradeApparence } from '@/components/config/GradeApparence';
import { GradeBoutique } from '@/components/config/GradeBoutique';
import styles from './GradesPage.module.scss';

export function GradesPage() {
  const [groups, setGroups] = useState<LuckPermsGroupDTO[]>([]);
  const [protectedGrades, setProtectedGrades] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  // Création de grade
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const loadGroups = useCallback(async () => {
    const res = await apiService.getAllGroups();
    if (res.data) {
      const sorted = [...res.data.groups].sort((a, b) => a.name.localeCompare(b.name));
      setGroups(sorted);
      setProtectedGrades((res.data.protectedGrades ?? []).map((n) => n.toLowerCase()));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreateError(null);
    const res = await apiService.createGroup(name);
    if (res.error) {
      setCreateError(res.error);
      return;
    }
    setNewName('');
    setCreating(false);
    await loadGroups();
    setSelected(name.toLowerCase().replace(/\s+/g, '_'));
  };

  const handleDelete = async (name: string) => {
    if (!window.confirm(`Supprimer définitivement le grade "${name}" ?`)) return;
    const res = await apiService.deleteGroup(name);
    if (!res.error) {
      if (selected === name) setSelected(null);
      await loadGroups();
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Grades</h1>
        <p className={styles.subtitle}>
          Gérez les grades LuckPerms et leurs permissions. {!loading && `${groups.length} grade(s).`}
        </p>
      </header>

      <div className={styles.layout}>
        {/* Liste des grades */}
        <aside className={styles.list}>
          <div className={styles.listHeader}>
            <Award size={18} strokeWidth={2} />
            <span>Grades</span>
            <button className={styles.createBtn} onClick={() => { setCreating((v) => !v); setCreateError(null); }}>
              {creating ? <X size={16} /> : <Plus size={16} />}
            </button>
          </div>

          {creating && (
            <div className={styles.createForm}>
              <input
                className={styles.input}
                placeholder="Nom du grade (ex: modo)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                autoFocus
              />
              <button className={styles.primaryBtn} onClick={handleCreate} disabled={!newName.trim()}>
                Créer
              </button>
              {createError && <span className={styles.error}>{createError}</span>}
            </div>
          )}

          {loading ? (
            <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement...</span></div>
          ) : (
            <div className={styles.listScroll}>
              {groups.map((g) => (
                <button
                  key={g.name}
                  className={`${styles.row} ${selected === g.name ? styles.rowActive : ''}`}
                  onClick={() => setSelected(g.name)}
                >
                  <Award size={16} className={styles.rowIcon} />
                  <span className={styles.rowName}>{g.name}</span>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Détail : permissions / chat / boutique / apparence / création */}
        <section className={styles.detail}>
          {selected ? (
            <GradeDetail name={selected} allGroups={groups} protectedGrades={protectedGrades} onDelete={() => handleDelete(selected)} />
          ) : (
            <div className={styles.detailEmpty}>
              <Award size={48} strokeWidth={1.5} />
              <p>Sélectionne un grade pour gérer ses permissions et cosmetics.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function GradeDetail({ name, allGroups, protectedGrades, onDelete }: { name: string; allGroups: LuckPermsGroupDTO[]; protectedGrades: string[]; onDelete: () => void }) {
  const isAdmin = useAuthStore((s) => !!s.user?.isAdmin);
  const [nodes, setNodes] = useState<GroupNodeDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPerm, setNewPerm] = useState('');
  const [newValue, setNewValue] = useState(true);
  const [newInherit, setNewInherit] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'perms' | 'chat' | 'boutique' | 'apparence'>('perms');

  // Grade protégé (whitelist admin) → lecture seule, SAUF pour un admin panel.
  const isProtectedGrade = protectedGrades.includes(name.toLowerCase());
  const isProtected = isProtectedGrade && !isAdmin;

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getGroupPermissions(name);
    setNodes(res.data?.permissions ?? []);
    setLoading(false);
  }, [name]);

  useEffect(() => {
    load();
  }, [load]);

  const addPerm = async () => {
    const perm = newPerm.trim();
    if (!perm) return;
    setBusy(true);
    setError(null);
    const res = await apiService.addGroupPermission(name, perm, newValue);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setNewPerm('');
    setNewValue(true);
    await load();
  };

  const removePerm = async (node: GroupNodeDTO) => {
    const res = await apiService.removeGroupPermission(name, node.key, node.value);
    if (!res.error) await load();
  };

  const addInherit = async () => {
    if (!newInherit) return;
    setBusy(true);
    setError(null);
    const res = await apiService.addGroupPermission(name, `group.${newInherit}`, true);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setNewInherit('');
    await load();
  };

  // Sépare héritages (group.*) et vraies permissions
  const inheritances = nodes.filter((n) => n.key.startsWith('group.'));
  const permissions = nodes.filter((n) => !n.key.startsWith('group.'));

  return (
    <div className={styles.detailInner}>
      <div className={styles.detailHeader}>
        <div className={styles.detailIdentity}>
          <Award size={22} className={styles.detailIcon} />
          <span className={styles.detailName}>{name}</span>
        </div>
        {!isProtected && name !== 'default' && (
          <button className={styles.deleteBtn} onClick={onDelete}>
            <Trash2 size={16} /> Supprimer le grade
          </button>
        )}
      </div>

      <div className={styles.tabBar}>
        <button type="button" className={`${styles.tab} ${tab === 'perms' ? styles.tabActive : ''}`} onClick={() => setTab('perms')}>
          <ShieldCheck size={14} /> Permissions
        </button>
        <button type="button" className={`${styles.tab} ${tab === 'chat' ? styles.tabActive : ''}`} onClick={() => setTab('chat')}>
          <MessageSquare size={14} /> Chat
        </button>
        <button type="button" className={`${styles.tab} ${tab === 'boutique' ? styles.tabActive : ''}`} onClick={() => setTab('boutique')}>
          <Store size={14} /> Boutique
        </button>
        <button type="button" className={`${styles.tab} ${tab === 'apparence' ? styles.tabActive : ''}`} onClick={() => setTab('apparence')}>
          <Sparkles size={14} /> Apparence
        </button>
      </div>

      {tab === 'perms' && (
      <>
      {isProtected ? (
        <div className={styles.protectedNotice}>
          <ShieldCheck size={16} /> Le grade <strong>{name}</strong> est protégé : édition réservée aux admins (lecture seule).
        </div>
      ) : (
        <>
          {/* Ajout de permission */}
          <div className={styles.addForm}>
            <input
              className={styles.input}
              placeholder="Permission (ex: recube.fly, essentials.tp...)"
              value={newPerm}
              onChange={(e) => setNewPerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addPerm()}
            />
            <button
              type="button"
              className={`${styles.toggleBtn} ${newValue ? styles.toggleTrue : styles.toggleFalse}`}
              onClick={() => setNewValue((v) => !v)}
              title="Autoriser (true) / Interdire (false)"
            >
              {newValue ? 'true' : 'false'}
            </button>
            <button className={styles.primaryBtn} onClick={addPerm} disabled={busy || !newPerm.trim()}>
              <Plus size={16} /> Ajouter
            </button>
          </div>

          {/* Ajout d'héritage */}
          <div className={styles.addForm}>
            <select
              className={styles.input}
              value={newInherit}
              onChange={(e) => setNewInherit(e.target.value)}
            >
              <option value="">Hériter d'un grade…</option>
              {allGroups
                .filter((g) => g.name.toLowerCase() !== name.toLowerCase())
                .map((g) => (
                  <option key={g.name} value={g.name}>{g.name}</option>
                ))}
            </select>
            <button className={styles.primaryBtn} onClick={addInherit} disabled={busy || !newInherit}>
              <Plus size={16} /> Héritage
            </button>
          </div>

          {error && <span className={styles.error}>{error}</span>}
        </>
      )}

      {loading ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement...</span></div>
      ) : (
        <>
          <div className={styles.sectionLabel}><ShieldCheck size={14} /> Permissions ({permissions.length})</div>
          {permissions.length === 0 ? (
            <p className={styles.muted}>Aucune permission spécifique.</p>
          ) : (
            <ul className={styles.permList}>
              {permissions.map((n) => (
                <li key={n.key} className={styles.permRow}>
                  <span className={styles.permKey}>{n.key}</span>
                  <span className={`${styles.permValue} ${n.value ? styles.vTrue : styles.vFalse}`}>
                    {n.value ? <Check size={12} /> : <X size={12} />}
                    {n.value ? 'true' : 'false'}
                  </span>
                  {!isProtected && (
                    <button className={styles.removeBtn} onClick={() => removePerm(n)} title="Retirer">
                      <Trash2 size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {inheritances.length > 0 && (
            <>
              <div className={styles.sectionLabel}><Award size={14} /> Héritages</div>
              <ul className={styles.permList}>
                {inheritances.map((n) => (
                  <li key={n.key} className={styles.permRow}>
                    <span className={styles.permKey}>{n.key.replace('group.', '')}</span>
                    {!isProtected && (
                      <button className={styles.removeBtn} onClick={() => removePerm(n)} title="Retirer l'héritage">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
      </>
      )}

      {tab === 'chat' && <GradeEssentials grade={name} readOnly={isProtected} />}
      {tab === 'boutique' && <GradeBoutique grade={name} readOnly={isProtected} />}
      {tab === 'apparence' && <GradeApparence grade={name} readOnly={isProtected} />}
    </div>
  );
}

/** Section Essentials d'un grade : nb de homes + format de chat par container, avec aperçu Minecraft. */
function GradeEssentials({ grade, readOnly = false }: { grade: string; readOnly?: boolean }) {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [containers, setContainers] = useState<PackContainer[]>([]);
  const [containerId, setContainerId] = useState('');
  const [data, setData] = useState<EssentialsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [homeLimit, setHomeLimit] = useState('');
  const [chatFormat, setChatFormat] = useState('');
  const [premiumChatFormat, setPremiumChatFormat] = useState('');
  const [orig, setOrig] = useState('');
  const [applyingAll, setApplyingAll] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    apiService.getContainers().then((r) => {
      const list = (r.data?.containers ?? []).filter((c) => (c.project ?? 'paladium') === activeGameId);
      setContainers(list);
      setContainerId((prev) => (list.some((c) => c.id === prev) ? prev : list[0]?.id ?? ''));
    });
  }, [activeGameId]);

  useEffect(() => {
    if (!containerId) { setData(null); return; }
    let active = true;
    setLoading(true);
    setFeedback(null);
    apiService.getContainerEssentials(containerId).then((r) => {
      if (!active) return;
      const d = r.data ?? { found: false, file: null, groups: [] };
      setData(d);
      const g = d.groups.find((x) => x.name === grade);
      const disk = {
        homeLimit: g?.homeLimit != null ? String(g.homeLimit) : '',
        chatFormat: g?.chatFormat ?? '',
        premiumChatFormat: g?.premiumChatFormat ?? '',
      };
      setOrig(JSON.stringify(disk));
      const pending = getDraftPayload(draftKey('grade-essentials', containerId, grade)) as typeof disk | undefined;
      setHomeLimit(pending?.homeLimit ?? disk.homeLimit);
      setChatFormat(pending?.chatFormat ?? disk.chatFormat);
      setPremiumChatFormat(pending?.premiumChatFormat ?? disk.premiumChatFormat);
      setLoading(false);
    });
    return () => { active = false; };
  }, [containerId, grade]);

  const container = containers.find((c) => c.id === containerId);
  const ctx = { hostname: container?.type ?? container?.id ?? 'server', player: 'Steve', faction: 'MaFaction', message: 'Salut !' };
  const dirty = !readOnly && !!data?.found && JSON.stringify({ homeLimit, chatFormat, premiumChatFormat }) !== orig;

  const save = async () => {
    const res = await apiService.saveContainerEssentialsGroup(
      containerId,
      grade,
      homeLimit.trim() === '' ? null : Number(homeLimit),
      chatFormat,
      premiumChatFormat,
    );
    const ok = !!res.data?.success;
    if (ok) setOrig(JSON.stringify({ homeLimit, chatFormat, premiumChatFormat }));
    return ok;
  };

  useSnapshotDraft({
    kind: 'grade-essentials',
    groupId: containerId,
    identifier: grade,
    label: `Chat · ${grade} · ${container?.name ?? containerId}`,
    dirty,
    payload: { homeLimit, chatFormat, premiumChatFormat },
    save,
  });

  const applyAll = async () => {
    if (!window.confirm(`Appliquer cette config (homes + chat) du grade « ${grade} » à TOUS les containers (${containers.length}) ? Les containers non synchronisés seront ignorés.`)) return;
    setApplyingAll(true);
    setFeedback(null);
    let ok = 0;
    let fail = 0;
    for (const c of containers) {
      const res = await apiService.saveContainerEssentialsGroup(
        c.id, grade, homeLimit.trim() === '' ? null : Number(homeLimit), chatFormat, premiumChatFormat,
      );
      if (res.data?.success) ok++;
      else fail++;
    }
    if (fail === 0) setOrig(JSON.stringify({ homeLimit, chatFormat, premiumChatFormat }));
    setApplyingAll(false);
    setFeedback({
      ok: fail === 0,
      msg: fail === 0
        ? `Appliqué à ${ok} container(s). Déploie chacun via « Serveurs ».`
        : `${ok} appliqué(s), ${fail} ignoré(s)/échec (container non synchronisé ?).`,
    });
  };

  if (containers.length === 0) return null;

  return (
    <div className={styles.essBlock}>
      <div className={styles.sectionLabel}><MessageSquare size={14} /> Essentials — chat &amp; homes</div>

      <div className={styles.essBar}>
        <Server size={14} />
        <select className={styles.input} value={containerId} onChange={(e) => setContainerId(e.target.value)}>
          {containers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement…</span></div>
      ) : !data?.found ? (
        <p className={styles.muted}>Config essentials introuvable — synchronise ce container dans « Serveurs ».</p>
      ) : (
        <>
          {readOnly && <div className={styles.protectedNotice}><ShieldCheck size={16} /> Grade protégé : édition réservée aux admins.</div>}
          <div className={styles.essField}>
            <label className={styles.essLabel}><Home size={13} /> Nombre de homes</label>
            <input className={styles.input} type="number" min={0} value={homeLimit} disabled={readOnly} onChange={(e) => setHomeLimit(e.target.value)} placeholder="ex. 18" />
          </div>

          <div className={styles.essField}>
            <label className={styles.essLabel}>Format du chat</label>
            <input className={styles.input} value={chatFormat} disabled={readOnly} onChange={(e) => setChatFormat(e.target.value)} placeholder="§7[{hostname}]…" spellCheck={false} />
            <div className={styles.essPreview}><MinecraftText text={chatFormat} context={ctx} /></div>
          </div>

          <div className={styles.essField}>
            <label className={styles.essLabel}>Format du chat premium (optionnel)</label>
            <input className={styles.input} value={premiumChatFormat} disabled={readOnly} onChange={(e) => setPremiumChatFormat(e.target.value)} placeholder="(vide = aucun)" spellCheck={false} />
            {premiumChatFormat.trim() !== '' && <div className={styles.essPreview}><MinecraftText text={premiumChatFormat} context={ctx} /></div>}
          </div>

          {feedback && <span className={feedback.ok ? styles.okMsg : styles.error}>{feedback.msg}</span>}

          {!readOnly && (
          <div className={styles.essActions}>
            <button className={styles.secondaryBtn} onClick={applyAll} disabled={applyingAll}>
              {applyingAll ? <Loader2 size={14} className={styles.spin} /> : <Copy size={14} />} Appliquer à tous les containers
            </button>
          </div>
          )}
          <p className={styles.muted}>
            {dirty ? 'Modifications en attente — enregistre-les via la barre du bas.' : 'Enregistré.'} Déploie ensuite chaque container via « Serveurs ».
          </p>
        </>
      )}
    </div>
  );
}
